/**
 * The shared playing-card MODEL — the basic card mechanics every card game in
 * this repo builds on. A standard 52-card deck + a DETERMINISTIC seeded shuffle,
 * so a given seed always produces the same deal everywhere (the whole point of a
 * community-synced "Deal of the Day").
 *
 * This is the stable seam: keep the `Card` shape and the shuffle behaviour fixed.
 * Relocated here from `games/solitaire/engine/cards.ts` so it is genuinely
 * shared — Solitaire's engines and any future card game import it from `@/cards`.
 */

export type Suit = 'spades' | 'hearts' | 'diamonds' | 'clubs';
export type Color = 'black' | 'red';
/** 1 = Ace, 11 = Jack, 12 = Queen, 13 = King. */
export type Rank = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13;

export interface Card {
  /** Stable id, e.g. "H7", "ST", "DA". Spider's 2nd deck suffixes "#1". */
  id: string;
  suit: Suit;
  rank: Rank;
  color: Color;
  /** Face-up to the player? Engines flip this; the renderer reads it. */
  faceUp: boolean;
}

export const SUITS: readonly Suit[] = ['spades', 'hearts', 'diamonds', 'clubs'];

export function colorOf(suit: Suit): Color {
  return suit === 'hearts' || suit === 'diamonds' ? 'red' : 'black';
}

const RANK_CHARS = ['', 'A', '2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K'];
const SUIT_CHARS: Record<Suit, string> = { spades: 'S', hearts: 'H', diamonds: 'D', clubs: 'C' };

export function cardId(suit: Suit, rank: Rank): string {
  return `${SUIT_CHARS[suit]}${RANK_CHARS[rank]}`;
}

/** One fresh, ordered deck (all face-down). `decks = 2` for Spider. */
export function makeDeck(decks = 1): Card[] {
  const out: Card[] = [];
  for (let d = 0; d < decks; d += 1) {
    for (const suit of SUITS) {
      for (let rank = 1; rank <= 13; rank += 1) {
        const r = rank as Rank;
        out.push({
          id: decks > 1 ? `${cardId(suit, r)}#${d}` : cardId(suit, r),
          suit,
          rank: r,
          color: colorOf(suit),
          faceUp: false,
        });
      }
    }
  }
  return out;
}

/** mulberry32 — tiny, fast, deterministic PRNG. Same seed → same sequence. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a hash → 32-bit seed, so a string ("20260601-klondike") seeds a deal. */
export function seedFrom(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Deterministic Fisher–Yates using a seed. Returns a NEW shuffled array. */
export function seededShuffle<T>(items: readonly T[], seed: number): T[] {
  const rng = mulberry32(seed);
  const a = items.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = a[i]!;
    a[i] = a[j]!;
    a[j] = tmp;
  }
  return a;
}
